import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { useMemo } from 'react';

export type LanguageCode = 'en' | 'es';

export const SUPPORTED_LANGUAGES: LanguageCode[] = ['en', 'es'];
export const DEFAULT_LANGUAGE: LanguageCode = 'es';

/**
 * Safely converts a string to LanguageCode with fallback to DEFAULT_LANGUAGE
 * @param language - Language string to convert (typically from i18n.language)
 * @returns Valid LanguageCode, defaulting to DEFAULT_LANGUAGE if invalid
 */
export function safeLanguageCode(language: string): LanguageCode {
  if (SUPPORTED_LANGUAGES.includes(language as LanguageCode)) {
    return language as LanguageCode;
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Safely extract language code from pathname using SUPPORTED_LANGUAGES validation
 * @param pathname - Full pathname (e.g., '/es/registrarse' or '/en/register')
 * @returns Extracted language code or null if not found/invalid
 */
export function extractLanguageFromPath(pathname: string): LanguageCode | null {
  if (!pathname || typeof pathname !== 'string') {
    return null;
  }
  
  // Match language code at start of path
  const match = pathname.match(/^\/([a-z]{2})/);
  if (!match) {
    return null;
  }
  
  const langCandidate = match[1];
  return SUPPORTED_LANGUAGES.includes(langCandidate as LanguageCode) 
    ? langCandidate as LanguageCode 
    : null;
}

/**
 * Safely remove language prefix from pathname with validation
 * @param pathname - Full pathname (e.g., '/es/registrarse')
 * @returns Path without language prefix, or original path if no valid language found
 */
export function removeLanguagePrefix(pathname: string): string {
  if (!pathname || typeof pathname !== 'string') {
    return '/';
  }
  
  const extractedLang = extractLanguageFromPath(pathname);
  if (extractedLang) {
    return pathname.replace(`/${extractedLang}`, '') || '/';
  }
  
  return pathname;
}

// Route mappings for different languages
const ROUTE_MAPPINGS: Record<LanguageCode, Record<string, string>> = {
  es: {
    home: '/',
    register: '/registrarse',
    privacyPolicy: '/politica-de-privacidad',
    legalDisclaimer: '/aviso-legal',
    faq: '/preguntas-frecuentes',
    whyMonitoringMatters: '/por-que-monitorear-importa',
    ourCommitment: '/nuestro-compromiso',
    howItWorks: '/como-funciona',
    mentions: '/menciones',
    accountPreferences: '/preferencias-de-cuenta',
    monitoredIdentities: '/identidades-monitoreadas',
    emailConfirmation: '/confirmar-email',
    unsubscribe: '/darse-de-baja',
    authVerify: '/auth/verify',
    emailConfirmed: '/email-confirmado',
    confirmEmailWithToken: '/confirmar-email/:token',
    unsubscribeWithToken: '/darse-de-baja/:token',
    adminDashboard: '/admin',
    adminUserManagement: '/admin/gestion-de-usuarios',
    adminEmailQueue: '/admin/cola-de-correos',
    adminPublications: '/admin/publicaciones',
    adminPublicationDetail: '/admin/publicaciones/:id',
    adminSettings: '/admin/configuracion',
    adminReports: '/admin/reportes'
  },
  en: {
    home: '/',
    register: '/register',
    privacyPolicy: '/privacy-policy',
    legalDisclaimer: '/legal-disclaimer',
    faq: '/faq',
    whyMonitoringMatters: '/why-monitoring-matters',
    ourCommitment: '/our-commitment',
    howItWorks: '/how-it-works',
    mentions: '/mentions',
    accountPreferences: '/account-preferences',
    monitoredIdentities: '/monitored-identities',
    emailConfirmation: '/email-confirmation',
    unsubscribe: '/unsubscribe',
    authVerify: '/auth/verify',
    emailConfirmed: '/email-confirmed',
    confirmEmailWithToken: '/confirm-email/:token',
    unsubscribeWithToken: '/unsubscribe/:token',
    adminDashboard: '/admin',
    adminUserManagement: '/admin/user-management',
    adminEmailQueue: '/admin/email-queue',
    adminPublications: '/admin/publications',
    adminPublicationDetail: '/admin/publications/:id',
    adminSettings: '/admin/settings',
    adminReports: '/admin/reports'
  }
};

/**
 * Hook to get current language from URL
 */
export function useCurrentLanguage(): LanguageCode {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  
  if (lang && SUPPORTED_LANGUAGES.includes(lang as LanguageCode)) {
    return lang as LanguageCode;
  }
  
  return safeLanguageCode(i18n.language);
}

/**
 * Hook to build language-prefixed URLs
 */
export function useLanguageUrl() {
  const currentLang = useCurrentLanguage();
  
  // Memoize the URL builder function to prevent unnecessary recalculations
  return useMemo(() => {
    return (routeKey: string, params?: Record<string, string>) => {
      let path = ROUTE_MAPPINGS[currentLang]?.[routeKey] || ROUTE_MAPPINGS[DEFAULT_LANGUAGE][routeKey] || '/';
      
      // Replace route parameters if provided
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          path = path.replace(`:${key}`, value);
        });
      }
      
      return `/${currentLang}${path}`;
    };
  }, [currentLang]); // Only recalculate when language actually changes
}

/**
 * Hook to handle language changes with URL update
 */
export function useLanguageChange() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const currentLang = useCurrentLanguage();
  
  return (newLang: LanguageCode) => {
    // Change the i18n language
    i18n.changeLanguage(newLang);
    
    // Update URL to use new language
    if (newLang !== currentLang) {
      const pathWithoutLang = location.pathname.replace(`/${currentLang}`, '');
      const newPath = `/${newLang}${pathWithoutLang}${location.search}${location.hash}`;
      
      // Use replace to avoid adding to history
      window.history.replaceState({}, '', newPath);
    }
  };
}

/**
 * Get the route path for a specific language without the language prefix
 */
export function getRouteForLanguage(routeKey: string, lang: LanguageCode): string {
  return ROUTE_MAPPINGS[lang]?.[routeKey] || ROUTE_MAPPINGS[DEFAULT_LANGUAGE][routeKey] || '/';
}

/**
 * Find the route key for a given path in a specific language
 * @param path - Path without language prefix (e.g., '/registrarse' or '/register')
 * @param lang - Language code to search in
 * @returns Route key (e.g., 'register') or null if not found
 */
export function findRouteKey(path: string, lang: LanguageCode): string | null {
  const routes = ROUTE_MAPPINGS[lang];
  
  // Remove trailing slash for comparison if not root
  const normalizedPath = path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
  
  for (const [key, routePath] of Object.entries(routes)) {
    // Normalize route path for comparison (remove :token params)
    const normalizedRoutePath = routePath.replace(/\/:[^/]+/g, '');
    if (normalizedRoutePath === normalizedPath) {
      return key;
    }
  }
  
  return null;
}

/**
 * Translate a path from one language to another
 * @param path - Path without language prefix in source language (e.g., '/politica-de-privacidad')
 * @param sourceLang - Source language code
 * @param targetLang - Target language code
 * @returns Translated path in target language (e.g., '/privacy-policy') or original path if no mapping exists
 */
export function translatePath(path: string, sourceLang: LanguageCode, targetLang: LanguageCode): string {
  // Handle root path
  if (path === '/' || path === '') {
    return '/';
  }
  
  // Find the route key in the source language
  const routeKey = findRouteKey(path, sourceLang);
  
  // If no route key found, return original path (might be a dynamic route)
  if (!routeKey) {
    return path;
  }
  
  // Get the path in the target language
  return getRouteForLanguage(routeKey, targetLang);
}

/**
 * Map of duplicate routes to their canonical equivalents (Spanish versions)
 * Used for SEO canonical URL consolidation
 */
const CANONICAL_ROUTE_MAPPING: Record<string, string> = {
  // English routes that should canonicalize to Spanish equivalents
  '/register': '/registrarse',
  '/mentions': '/menciones',
  '/privacy-policy': '/politica-de-privacidad',
  '/legal-disclaimer': '/aviso-legal',
  '/faq': '/preguntas-frecuentes',
  '/why-monitoring-matters': '/por-que-monitorear-importa',
  '/our-commitment': '/nuestro-compromiso',
  '/how-it-works': '/como-funciona',
  '/account-preferences': '/preferencias-de-cuenta',
  '/monitored-identities': '/identidades-monitoreadas',
  '/email-confirmation': '/confirmar-email',
  '/unsubscribe': '/darse-de-baja',
  '/email-confirmed': '/email-confirmado',
  '/confirm-email': '/confirmar-email',
  // Token-based routes
  '/confirm-email/:token': '/confirmar-email/:token',
  '/unsubscribe/:token': '/darse-de-baja/:token'
};

/**
 * Get canonical route mapping for duplicate route consolidation
 * @param path - Path without language prefix (e.g., '/register')
 * @returns Canonical path (e.g., '/registrarse') or original path if already canonical
 */
export function getCanonicalRouteMapping(path: string): string {
  return CANONICAL_ROUTE_MAPPING[path] || path;
}

/**
 * Check if a route is the canonical version (Spanish) vs secondary (English)
 * @param path - Path without language prefix (e.g., '/registrarse' or '/register')
 * @returns True if this is the canonical route, false if it should redirect to canonical
 */
export function isCanonicalRoute(path: string): boolean {
  // Use safe language removal function instead of vulnerable regex
  const cleanPath = removeLanguagePrefix(path);
  
  // If the path is in the canonical mapping values (Spanish routes), it's canonical
  const canonicalRoutes = Object.values(CANONICAL_ROUTE_MAPPING);
  if (canonicalRoutes.includes(cleanPath)) {
    return true;
  }
  
  // If the path is in the canonical mapping keys (English routes), it's not canonical
  if (CANONICAL_ROUTE_MAPPING[cleanPath]) {
    return false;
  }
  
  // If path is not in mapping at all, assume it's canonical
  return true;
}

/**
 * Get the canonical version of a path for SEO purposes
 * @param pathname - Full pathname with language (e.g., '/en/register')
 * @returns Canonical pathname (e.g., '/es/registrarse')
 */
export function getCanonicalPath(pathname: string): string {
  // Use safe language removal function instead of vulnerable regex
  const pathWithoutLang = removeLanguagePrefix(pathname);
  const canonicalPath = getCanonicalRouteMapping(pathWithoutLang);
  return `/${DEFAULT_LANGUAGE}${canonicalPath}`;
}